// client/src/pages/AdminFeatureFlags.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import { API_BASE_URL } from '../utils/media';
import {
  Box, Typography, List, ListItem, ListItemText, Switch, Alert, CircularProgress, Paper,
} from '@mui/material';

const AdminFeatureFlags = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useContext(AuthContext);
  const { refreshFlags } = useContext(FeatureFlagContext);

  const fetchFlags = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/feature-flags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch feature flags.');
      setFlags(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchFlags(); }, [fetchFlags]);

  const handleToggle = async (flag) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/feature-flags/${flag.flag_key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: !flag.enabled }),
      });
      if (!response.ok) throw new Error('Failed to update feature flag.');
      await fetchFlags();
      await refreshFlags(); // Applies immediately across the site, not just this admin session.
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Site Features</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Turn optional features on or off across the whole website. Disabled features are hidden from
        navigation and their pages redirect home.
      </Typography>

      <Paper variant="outlined">
        <List disablePadding>
          {flags.map((flag, index) => (
            <ListItem
              key={flag.id}
              divider={index < flags.length - 1}
              secondaryAction={<Switch checked={Boolean(flag.enabled)} onChange={() => handleToggle(flag)} />}
            >
              <ListItemText primary={flag.label} secondary={flag.description} />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default AdminFeatureFlags;
